import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Shield, Users, DollarSign, TrendingUp, Search, Download, Crown, UserPlus, Megaphone, RefreshCw, XCircle, Activity, Send, Ticket, LineChart as LineChartIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

const K = ({ label, value, sub, color }) => (
  <div className="glass-card p-5">
    <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">{label}</div>
    <div className="font-mono text-2xl font-bold" style={{ color: color || "var(--text-primary)" }}>{value}</div>
    {sub && <div className="text-xs text-[color:var(--text-secondary)] mt-1">{sub}</div>}
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const [check, setCheck] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [refs, setRefs] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [cancels, setCancels] = useState([]);
  const [live, setLive] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [mrrRet, setMrrRet] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", percent_off: "", duration: "once", duration_in_months: 3, max_redemptions: "" });
  const [tier, setTier] = useState("all");
  const [search, setSearch] = useState("");
  const [annForm, setAnnForm] = useState({ title: "", body: "", audience: "all", tone: "info" });

  useEffect(() => { api.get("/admin/me").then(r => setCheck(r.data.is_admin)).catch(() => setCheck(false)); }, []);
  useEffect(() => {
    if (!check) return;
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/admin/growth").then(r => setGrowth(r.data.weeks));
    api.get("/admin/top-referrers").then(r => setRefs(r.data.top));
    api.get("/admin/cohorts").then(r => setCohorts(r.data.cohorts));
    api.get("/admin/cancellations").then(r => setCancels(r.data.cancellations));
    api.get("/admin/announcements").then(r => setAnnouncements(r.data.announcements));
    api.get("/admin/coupons").then(r => setCoupons(r.data.coupons));
    api.get("/admin/mrr-retention").then(r => setMrrRet(r.data.cohorts));
    const tick = () => api.get("/admin/live").then(r => setLive(r.data)).catch(() => {});
    tick(); const t = setInterval(tick, 15000); return () => clearInterval(t);
  }, [check]);
  useEffect(() => {
    if (!check) return;
    const params = { tier };
    if (search.trim()) params.search = search.trim();
    api.get("/admin/users", { params }).then(r => setUsers(r.data.users));
  }, [check, tier, search]);

  const downloadCsv = async () => {
    const token = localStorage.getItem("arm_token");
    const res = await fetch(`${api.defaults.baseURL}/admin/users.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "afrorichmind-users.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const maxSignup = useMemo(() => Math.max(1, ...growth.map(w => w.signups)), [growth]);

  const sendAnnouncement = async () => {
    if (!annForm.title.trim() || !annForm.body.trim()) { toast.error("Title and body required"); return; }
    try {
      const r = await api.post("/admin/announcements", annForm);
      toast.success(`Sent to ${r.data.reach} users`);
      setAnnForm({ title: "", body: "", audience: "all", tone: "info" });
      const list = await api.get("/admin/announcements"); setAnnouncements(list.data.announcements);
    } catch (e) { toast.error("Failed to send"); }
  };

  const refund = async (subId, email) => {
    const amt = window.prompt(`Refund ${email}. Amount in $ (empty for full):`);
    if (amt === null) return;
    // For MVP we look up the latest charge via subscription; here we support charge_id direct entry:
    const cid = window.prompt("Stripe charge_id (ch_...) or leave empty to use latest payment intent");
    try {
      const r = await api.post("/admin/refund", { charge_id: cid || undefined, amount: amt ? parseFloat(amt) : undefined, reason: "admin_manual" });
      toast.success(`Refunded $${r.data.amount}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const createCoupon = async () => {
    if (!couponForm.code.trim() || !couponForm.percent_off) { toast.error("Code + percent off required"); return; }
    try {
      await api.post("/admin/coupons", {
        code: couponForm.code, percent_off: parseFloat(couponForm.percent_off),
        duration: couponForm.duration,
        duration_in_months: couponForm.duration === "repeating" ? parseInt(couponForm.duration_in_months) : undefined,
        max_redemptions: couponForm.max_redemptions ? parseInt(couponForm.max_redemptions) : undefined,
      });
      toast.success(`Coupon ${couponForm.code.toUpperCase()} created`);
      setCouponForm({ code: "", percent_off: "", duration: "once", duration_in_months: 3, max_redemptions: "" });
      const r = await api.get("/admin/coupons"); setCoupons(r.data.coupons);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (check === null) return <div className="p-8">Checking permissions...</div>;
  if (check === false) return <Navigate to="/dashboard" replace />;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-[color:var(--gold)]" />
        <h1 className="font-heading text-3xl font-bold">Admin Dashboard</h1>
      </div>
      <p className="text-[color:var(--text-secondary)] -mt-2">Live view of every subscriber, dollar, and conversion.</p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <K label="MRR" value={`$${stats.mrr.toLocaleString()}`} sub={`ARR ~$${Math.round(stats.arr).toLocaleString()}`} color="var(--gold)" />
          <K label="Revenue" value={`$${stats.total_revenue.toLocaleString()}`} sub="all-time" color="#2A9D8F" />
          <K label="Paying" value={stats.paying} sub={`${stats.conversion_rate}% conversion`} color="var(--gold)" />
          <K label="Total users" value={stats.total_users} sub={`+${stats.new_this_week} this week`} />
          <K label="Free" value={stats.free} sub={`${stats.premium} Prem · ${stats.premium_plus} Prem+`} />
        </div>
      )}

      {/* Growth Chart */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[color:var(--gold)]" />
            <h2 className="font-heading text-lg font-semibold">Weekly signups (last 12 weeks)</h2>
          </div>
        </div>
        <div className="flex items-end gap-2 h-32">
          {growth.map((w, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col justify-end h-full relative">
                <motion.div initial={{ height: 0 }} animate={{ height: `${(w.signups / maxSignup) * 100}%` }} transition={{ duration: 0.6, delay: i * 0.03 }}
                  className="w-full bg-gradient-to-t from-[#B38728] to-[#D4AF37] rounded-t-md relative">
                  {w.paying > 0 && (
                    <div className="absolute bottom-0 left-0 w-full bg-[color:var(--emerald)] rounded-t-md"
                      style={{ height: `${(w.paying / w.signups) * 100}%` }} title={`${w.paying} paying`} />
                  )}
                </motion.div>
              </div>
              <div className="text-[9px] text-[color:var(--text-muted)] font-mono">{w.week}</div>
              <div className="text-[10px] font-mono font-semibold">{w.signups}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-4 text-xs text-[color:var(--text-secondary)] mt-3">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-gradient-to-t from-[#B38728] to-[#D4AF37]" /> Signups</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[color:var(--emerald)]" /> Converted to paying</span>
        </div>
      </div>

      {/* Users table */}
      <div className="glass-card p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1">
            <Users className="w-4 h-4 text-[color:var(--gold)]" />
            <h2 className="font-heading text-lg font-semibold">Subscribers</h2>
            <span className="text-xs text-[color:var(--text-muted)]">({users.length})</span>
          </div>
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input data-testid="admin-search" placeholder="Search name or email" value={search} onChange={e => setSearch(e.target.value)}
                className="pl-8 pr-3 py-2 rounded-full bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none text-xs" />
            </div>
            <select data-testid="admin-tier-filter" value={tier} onChange={e => setTier(e.target.value)}
              className="px-3 py-2 rounded-full bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none text-xs">
              <option value="all">All tiers</option>
              <option value="paying">Paying only</option>
              <option value="premium">Premium</option>
              <option value="premium_plus">Premium+</option>
              <option value="free">Free</option>
            </select>
            <button data-testid="admin-csv" onClick={downloadCsv} className="gold-btn px-3 py-2 rounded-full text-xs inline-flex items-center gap-1">
              <Download className="w-3 h-3" /> CSV
            </button>
          </div>
        </div>

        <div className="overflow-auto scroll-fade">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] border-b border-[rgba(212,175,55,0.1)]">
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Tier</th>
                <th className="text-left p-2">Signup</th>
                <th className="text-left p-2">Method</th>
                <th className="text-right p-2">Referred</th>
                <th className="text-right p-2">Payments</th>
              </tr>
            </thead>
            <tbody data-testid="admin-users-tbody">
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-[rgba(212,175,55,0.05)] hover:bg-[rgba(212,175,55,0.03)]">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center text-[color:var(--gold)] text-xs font-semibold">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm">{u.name}</div>
                        <div className="text-[10px] text-[color:var(--text-muted)]">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-2">
                    {u.tier === "premium_plus" ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(212,175,55,0.2)] text-[color:var(--gold)] uppercase tracking-widest inline-flex items-center gap-1"><Crown className="w-2.5 h-2.5" /> Premium+</span>
                      : u.tier === "premium" ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(212,175,55,0.1)] text-[color:var(--gold)] uppercase tracking-widest">Premium</span>
                        : <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(255,255,255,0.05)] text-[color:var(--text-muted)] uppercase tracking-widest">Free</span>}
                  </td>
                  <td className="p-2 text-xs text-[color:var(--text-secondary)]">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="p-2 text-xs text-[color:var(--text-secondary)] capitalize">{u.method}</td>
                  <td className="p-2 text-right font-mono text-xs">{u.referred_count}</td>
                  <td className="p-2 text-right font-mono text-xs">{u.paid_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <div className="p-8 text-center text-sm text-[color:var(--text-muted)]">No users match</div>}
        </div>
      </div>

      {refs.length > 0 && (
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-[color:var(--gold)]" />
            <h2 className="font-heading text-lg font-semibold">Top referrers</h2>
          </div>
          <div className="space-y-2">
            {refs.map((r, i) => (
              <div key={r.user_id} className="flex items-center gap-3 p-2">
                <div className="w-6 text-center font-mono text-[color:var(--text-muted)]">#{i + 1}</div>
                <div className="flex-1"><div className="text-sm">{r.name}</div><div className="text-[10px] text-[color:var(--text-muted)]">{r.email}</div></div>
                <div className="font-mono text-sm">{r.invited} <span className="text-[10px] text-[color:var(--text-muted)] uppercase">invited</span></div>
                <div className="font-mono text-sm text-[color:var(--emerald)]">{r.converted} <span className="text-[10px] text-[color:var(--text-muted)] uppercase">paid</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LIVE TICKER */}
      {live && (
        <div className="glass-card p-6 border-[rgba(42,157,143,0.25)]">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-[color:var(--emerald)] animate-pulse" />
            <h2 className="font-heading text-lg font-semibold">Live · last 24h</h2>
            <span className="text-[10px] text-[color:var(--text-muted)] ml-auto">refreshes every 15s</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <K label="Revenue 24h" value={`$${live.revenue_24h}`} color="var(--emerald)" />
            <K label="Payments 24h" value={live.events_24h} />
            <K label="Signups 24h" value={live.signups_24h} />
          </div>
          {live.recent_events.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Recent payments</div>
              {live.recent_events.map((e, i) => (
                <div key={i} className="flex items-center gap-3 text-xs p-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                  <DollarSign className="w-3 h-3 text-[color:var(--emerald)]" />
                  <span className="font-mono font-semibold text-[color:var(--emerald)]">${e.amount}</span>
                  <span className="flex-1 text-[color:var(--text-secondary)]">{e.customer_email || "customer"}</span>
                  <span className="text-[color:var(--text-muted)]">{new Date(e.created_at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* COHORT RETENTION */}
      {cohorts.length > 0 && (
        <div className="glass-card p-6 overflow-auto">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[color:var(--gold)]" />
            <h2 className="font-heading text-lg font-semibold">Cohort retention (paying %)</h2>
          </div>
          <table className="text-xs">
            <thead>
              <tr className="text-[10px] uppercase text-[color:var(--text-muted)]">
                <th className="p-2 text-left">Cohort</th><th className="p-2">Size</th>
                {[0,1,2,3,4,5,6,7,8,9,10,11].map(w => <th key={w} className="p-2 min-w-[44px]">W{w}</th>)}
              </tr>
            </thead>
            <tbody>
              {cohorts.map(row => (
                <tr key={row.week} className="border-t border-[rgba(212,175,55,0.08)]">
                  <td className="p-2 font-mono">{row.week}</td>
                  <td className="p-2 font-mono text-center">{row.size}</td>
                  {[0,1,2,3,4,5,6,7,8,9,10,11].map(w => {
                    const cell = row.cells.find(c => c.week === w);
                    if (!cell) return <td key={w} className="p-2" />;
                    const intensity = Math.min(1, cell.pct / 40);
                    return <td key={w} className="p-2 text-center font-mono text-[10px]" style={{ background: `rgba(212,175,55,${intensity * 0.4})` }}>{cell.pct}%</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ANNOUNCEMENTS */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone className="w-4 h-4 text-[color:var(--gold)]" />
            <h2 className="font-heading text-lg font-semibold">Push announcement</h2>
          </div>
          <div className="space-y-2">
            <input data-testid="ann-title" placeholder="Title" value={annForm.title} onChange={e => setAnnForm({ ...annForm, title: e.target.value })}
              className="w-full px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none text-sm" />
            <textarea data-testid="ann-body" placeholder="Body (1-2 short sentences)" rows="3" value={annForm.body} onChange={e => setAnnForm({ ...annForm, body: e.target.value })}
              className="w-full px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <select value={annForm.audience} onChange={e => setAnnForm({ ...annForm, audience: e.target.value })}
                className="px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] text-xs">
                <option value="all">Everyone</option><option value="free">Free only</option>
                <option value="paying">Paying only</option><option value="premium">Premium only</option><option value="premium_plus">Premium+ only</option>
              </select>
              <select value={annForm.tone} onChange={e => setAnnForm({ ...annForm, tone: e.target.value })}
                className="px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] text-xs">
                <option value="info">Info</option><option value="positive">Positive</option><option value="warning">Warning</option><option value="action">Action</option>
              </select>
            </div>
            <button data-testid="ann-send" onClick={sendAnnouncement} className="gold-btn w-full py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-2">
              <Send className="w-3 h-3" /> Send to selected audience
            </button>
          </div>
          {announcements.length > 0 && (
            <div className="mt-5 pt-4 border-t border-[rgba(212,175,55,0.1)] space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Recent</div>
              {announcements.slice(0, 5).map(a => (
                <div key={a.announcement_id} className="text-xs p-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                  <div className="font-medium">{a.title}</div>
                  <div className="text-[10px] text-[color:var(--text-muted)]">{a.audience} · reach {a.reach} · {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CANCELLATIONS */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <XCircle className="w-4 h-4 text-[color:var(--terracotta)]" />
            <h2 className="font-heading text-lg font-semibold">Cancellations & refunds</h2>
          </div>
          {cancels.length === 0 ? (
            <div className="text-sm text-[color:var(--text-muted)] py-4">No cancellations yet.</div>
          ) : (
            <div className="space-y-2">
              {cancels.map(c => (
                <div key={c.subscription_id} className="p-3 rounded-lg bg-[rgba(255,255,255,0.02)]">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-[10px] text-[color:var(--text-muted)]">{c.email}</div>
                      {c.reason && <div className="text-xs text-[color:var(--text-secondary)] mt-1 italic">"{c.reason}"</div>}
                    </div>
                    <button data-testid={`refund-${c.subscription_id}`} onClick={() => refund(c.subscription_id, c.email)}
                      className="text-[10px] px-3 py-1 rounded-full border border-[rgba(224,106,59,0.3)] hover:border-[color:var(--terracotta)] hover:text-[color:var(--terracotta)] transition inline-flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> Refund
                    </button>
                  </div>
                  <div className="text-[10px] text-[color:var(--text-muted)] mt-1">Cancels {c.cancel_at ? new Date(c.cancel_at).toLocaleDateString() : "now"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MRR RETENTION */}
      {mrrRet.length > 0 && (
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <LineChartIcon className="w-4 h-4 text-[color:var(--emerald)]" />
            <h2 className="font-heading text-lg font-semibold">$ MRR retention by cohort</h2>
          </div>
          <div className="space-y-2">
            {mrrRet.map((c) => (
              <div key={c.cohort} className="flex items-center gap-4 p-3 rounded-lg bg-[rgba(255,255,255,0.02)]">
                <div className="w-24 text-sm font-medium">{c.cohort}</div>
                <div className="text-xs text-[color:var(--text-muted)] w-24">{c.size} subs · start ${c.starting_mrr}</div>
                <div className="flex-1 h-2 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#2A9D8F]" style={{ width: `${Math.min(100, c.retention_pct)}%` }} />
                </div>
                <div className="font-mono text-sm font-bold gold-gradient-text w-16 text-right">${c.current_mrr}</div>
                <div className="font-mono text-xs w-14 text-right" style={{ color: c.retention_pct >= 80 ? "#2A9D8F" : c.retention_pct >= 50 ? "#E9C46A" : "#E06A3B" }}>{c.retention_pct}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* COUPONS */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Ticket className="w-4 h-4 text-[color:var(--gold)]" />
          <h2 className="font-heading text-lg font-semibold">Coupon codes</h2>
        </div>
        <div className="grid md:grid-cols-5 gap-2 mb-4">
          <input data-testid="coupon-code" placeholder="LAUNCH50" value={couponForm.code} onChange={e => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
            className="px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none text-xs uppercase font-mono" />
          <input data-testid="coupon-percent" type="number" placeholder="% off" value={couponForm.percent_off} onChange={e => setCouponForm({ ...couponForm, percent_off: e.target.value })}
            className="px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none text-xs" />
          <select value={couponForm.duration} onChange={e => setCouponForm({ ...couponForm, duration: e.target.value })}
            className="px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] text-xs">
            <option value="once">Once</option><option value="repeating">Repeating</option><option value="forever">Forever</option>
          </select>
          <input placeholder="Max uses" type="number" value={couponForm.max_redemptions} onChange={e => setCouponForm({ ...couponForm, max_redemptions: e.target.value })}
            className="px-3 py-2 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none text-xs" />
          <button data-testid="coupon-create" onClick={createCoupon} className="gold-btn px-4 py-2 rounded-full text-xs">Create</button>
        </div>
        {coupons.length > 0 && (
          <div className="space-y-1">
            {coupons.map(c => (
              <div key={c.coupon_id} className="flex items-center gap-3 p-2 text-xs rounded-lg bg-[rgba(255,255,255,0.02)]">
                <div className="font-mono font-bold gold-gradient-text w-32">{c.code}</div>
                <div className="text-[color:var(--text-secondary)]">{c.percent_off}% off · {c.duration}</div>
                <div className="flex-1" />
                <div className="text-[color:var(--text-muted)]">{c.times_redeemed || 0} used{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</div>
              </div>
            ))}
          </div>
        )}
        <div className="text-[10px] text-[color:var(--text-muted)] mt-3 border-t border-[rgba(212,175,55,0.08)] pt-3">
          Share: users get the discount when they enter the code in the "Have a coupon?" prompt during upgrade.
        </div>
      </div>
    </div>
  );
}
